"use client";

import React, { useMemo, useState } from "react";
import type { TradeOrder } from "@/vybe-trader-client/trade-history/types";
import {
  formatDealType,
  formatOrderType,
  orderTypeBuySellClass,
} from "@/vybe-trader-client/trade-history/types";
import { isPositionBalanceOperation } from "@/vybe-trader-client/trade-history/historySummary";
import {
  detectStopHit,
  formatDurationShort,
  positionCloseMs,
  positionOpenMs,
  positionPips,
  type StopHit,
} from "@/vybe-trader-client/trade-history/accountAnalytics";
import {
  formatDateTimeCompact,
  formatHistoryPrice,
  positionPriceChangeParts,
} from "@/components/history-view/content/historyDesktopTableHelpers";
import { formatHistoryLocalDateTimeParts } from "@/components/history-view/historyDateTimeFormat";
import {
  AnalyticsCard,
  AnalyticsCardHeader,
  fmtPips,
  pnlClass,
} from "./analyticsShared";

const PAGE_SIZE = 50;

const th =
  "px-1.5 h-7 text-left text-[10px] font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap";
const td = "px-1.5 whitespace-nowrap font-mono tabular-nums text-[11px]";

interface LedgerRow {
  trade: TradeOrder;
  openTime: string;
  closeTime: string;
  action: string;
  actionClass: string;
  isBalanceOp: boolean;
  pips: number;
  durationMs: number;
  hit: StopHit;
  change: { display: string; numeric: number | null };
}

/**
 * Trading Activity ledger — the 13 Myfxbook columns with SL/TP-hit cell
 * tints (a labelled heuristic: tolerance match / broker close reason).
 * Paginated so tens of thousands of rows stay cheap inside the modal.
 */
export default function AnalyticsActivityCard({
  positions,
}: {
  positions: TradeOrder[];
}) {
  const [page, setPage] = useState(0);

  const rows = useMemo<LedgerRow[]>(() => {
    // Newest first, like the History table.
    const sorted = [...positions].sort(
      (a, b) => positionCloseMs(b) - positionCloseMs(a)
    );
    return sorted.map((trade) => {
      const isBalanceOp = isPositionBalanceOperation(trade);
      const openMs = positionOpenMs(trade);
      const closeMs = positionCloseMs(trade);
      return {
        trade,
        openTime: formatDateTimeCompact(
          formatHistoryLocalDateTimeParts(trade.openTime)
        ),
        closeTime: formatDateTimeCompact(
          formatHistoryLocalDateTimeParts(trade.closeTime)
        ),
        action: isBalanceOp
          ? trade.profit >= 0
            ? "Deposit"
            : "Withdrawal"
          : formatOrderType(trade.orderType) === `Unknown (${trade.orderType})`
            ? formatDealType(trade.dealType)
            : formatOrderType(trade.orderType),
        actionClass: isBalanceOp
          ? "text-muted-foreground"
          : orderTypeBuySellClass(trade.orderType),
        isBalanceOp,
        pips: isBalanceOp ? 0 : positionPips(trade),
        durationMs: openMs > 0 && closeMs > openMs ? closeMs - openMs : 0,
        hit: isBalanceOp ? null : detectStopHit(trade),
        change: positionPriceChangeParts(trade, isBalanceOp),
      };
    });
  }, [positions]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE
  );

  return (
    <AnalyticsCard>
      <AnalyticsCardHeader>
        <span>Trading activity ({rows.length})</span>
        <span className="ml-auto normal-case tracking-normal">
          SL cell tinted when stopped · TP when hit (heuristic)
        </span>
      </AnalyticsCardHeader>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[11px]">
          <thead>
            <tr className="border-b border-border">
              <th className={th}>Open time</th>
              <th className={th}>Close time</th>
              <th className={th}>Symbol</th>
              <th className={th}>Action</th>
              <th className={`${th} text-right`}>Lots</th>
              <th className={`${th} text-right`}>SL</th>
              <th className={`${th} text-right`}>TP</th>
              <th className={`${th} text-right`}>Open</th>
              <th className={`${th} text-right`}>Close</th>
              <th className={`${th} text-right`}>Pips</th>
              <th className={`${th} text-right`}>Profit</th>
              <th className={`${th} text-right`}>Duration</th>
              <th className={`${th} text-right`}>Change %</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr>
                <td
                  colSpan={13}
                  className="py-4 text-center text-[11px] text-muted-foreground"
                >
                  No activity in this scope
                </td>
              </tr>
            )}
            {pageRows.map((row) => {
              const p = row.trade;
              const chgClass =
                row.change.numeric === null
                  ? "text-muted-foreground"
                  : pnlClass(row.change.numeric);
              return (
                <tr
                  key={p.ticket}
                  className="h-7 border-b border-border/40 transition-colors hover:bg-accent"
                >
                  <td className={`${td} text-muted-foreground`}>
                    {row.openTime}
                  </td>
                  <td className={`${td} text-muted-foreground`}>
                    {row.closeTime}
                  </td>
                  <td className={`${td} font-semibold`}>{p.symbol || "—"}</td>
                  <td className={`${td} ${row.actionClass}`}>{row.action}</td>
                  <td className={`${td} text-right`}>
                    {row.isBalanceOp ? "-" : p.lots}
                  </td>
                  <td
                    className={`${td} text-right ${
                      row.hit === "sl" ? "bg-red-400/10 text-red-400" : ""
                    }`}
                  >
                    {formatHistoryPrice(p.stopLoss, p.digits)}
                  </td>
                  <td
                    className={`${td} text-right ${
                      row.hit === "tp" ? "bg-green-500/10 text-green-500" : ""
                    }`}
                  >
                    {formatHistoryPrice(p.takeProfit, p.digits)}
                  </td>
                  <td className={`${td} text-right`}>
                    {row.isBalanceOp ? "-" : p.openPrice.toFixed(p.digits)}
                  </td>
                  <td className={`${td} text-right`}>
                    {row.isBalanceOp ? "-" : p.closePrice.toFixed(p.digits)}
                  </td>
                  <td className={`${td} text-right ${pnlClass(row.pips)}`}>
                    {row.isBalanceOp ? "-" : fmtPips(row.pips)}
                  </td>
                  <td
                    className={`${td} text-right font-semibold ${pnlClass(p.profit)}`}
                  >
                    {p.profit >= 0 ? "+" : ""}
                    {p.profit.toFixed(2)}
                  </td>
                  <td className={`${td} text-right text-muted-foreground`}>
                    {row.isBalanceOp ? "-" : formatDurationShort(row.durationMs)}
                  </td>
                  <td className={`${td} text-right ${chgClass}`}>
                    {row.change.display}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-1 px-2 py-1.5 font-mono text-[10px] text-muted-foreground">
          <button
            type="button"
            className="rounded border border-border px-1.5 py-0.5 hover:bg-accent disabled:opacity-40"
            disabled={safePage === 0}
            onClick={() => setPage(safePage - 1)}
          >
            ‹
          </button>
          <span className="px-1">
            {safePage + 1} / {pageCount}
          </span>
          <button
            type="button"
            className="rounded border border-border px-1.5 py-0.5 hover:bg-accent disabled:opacity-40"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage(safePage + 1)}
          >
            ›
          </button>
        </div>
      )}
    </AnalyticsCard>
  );
}
